import React from 'react';
import { BookOpen, FileText, Search, Monitor, Users, Video, GraduationCap, Award } from 'lucide-react';

const ServiceCard = ({ 
  icon: Icon, 
  title, 
  description, 
  color 
}: { 
  icon: React.ElementType; 
  title: string; 
  description: string; 
  color: string;
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2 group">
      <div className={`${color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
        <Icon className="h-8 w-8 text-white" />
      </div>
      <h3 className="text-xl font-bold mb-3 text-gray-800 group-hover:text-blue-800 transition-colors">
        {title}
      </h3>
      <p className="text-gray-600 leading-relaxed">
        {description}
      </p>
    </div>
  );
};

const Services = () => {
  const services = [
    {
      icon: BookOpen,
      title: "شرح المناهج",
      description: "شرح واضح ومفصل للمناهج الدراسية، نجعل المواضيع المعقدة سهلة الفهم والاستيعاب.",
      color: "bg-blue-800"
    },
    {
      icon: FileText,
      title: "مساعدة في الواجبات",
      description: "مساعدة متخصصة في حل الواجبات بدقة وثقة، لضمان تقدمك المستمر في دراستك.",
      color: "bg-teal-600"
    },
    {
      icon: Search,
      title: "دعم البحوث",
      description: "توجيه شامل في إعداد البحوث والمشاريع المدرسية عالية الجودة، من التخطيط إلى التسليم النهائي.",
      color: "bg-amber-500"
    },
    {
      icon: Monitor,
      title: "تحضير للاختبارات",
      description: "استراتيجيات فعالة وجلسات تدريبية مكثفة لمساعدتك في التفوق والنجاح في الاختبارات.",
      color: "bg-purple-600"
    }
  ];

  return (
    <section id="services" className="py-20 md:py-28 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* العنوان الرئيسي */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            خدماتنا التعليمية
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            يقدم فريقنا من المعلمين المتخصصين مجموعة شاملة من الخدمات الأكاديمية المصممة لتلبية احتياجاتك التعليمية.
          </p>
        </div>

        {/* قسم مؤهلات المدرسين */}
        <div className="mb-20 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-10 rounded-2xl shadow-xl">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center mb-6">
              <div className="bg-white/20 p-4 rounded-xl ml-4">
                <GraduationCap className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold">
                مدرسون مؤهلون وذوو خبرة
              </h3>
            </div>
            <p className="text-xl opacity-90 max-w-4xl mx-auto leading-relaxed">
              فريقنا من المدرسين حاصلون على جميع الشهادات الجامعية اللازمة للتعامل مع الطلاب وتبسيط المعلومات
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
              <div className="flex items-center mb-6">
                <GraduationCap className="h-8 w-8 ml-4" />
                <h4 className="text-2xl font-bold">مؤهلات أكاديمية عالية</h4>
              </div>
              <ul className="space-y-3 text-lg opacity-90">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  شهادات جامعية متخصصة في المواد التدريسية
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  مؤهلات تربوية معتمدة
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  تدريب مستمر على أحدث طرق التدريس
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  خبرة في تبسيط المعلومات المعقدة
                </li>
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-xl border border-white/20">
              <div className="flex items-center mb-6">
                <Award className="h-8 w-8 ml-4" />
                <h4 className="text-2xl font-bold">خبرة في التدريس عن بُعد</h4>
              </div>
              <ul className="space-y-3 text-lg opacity-90">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  سنوات من الخبرة في التدريس الإلكتروني
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  إتقان استخدام التقنيات التعليمية الحديثة
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  مهارات التواصل الفعال مع الطلاب
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-white rounded-full ml-3"></div>
                  قدرة على إشراك الطلاب وتحفيزهم
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* بطاقات الخدمات */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              color={service.color}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;